import { findFirstNonEmptyString } from "@product/scmp-sdk";
import first from "lodash/first";
import last from "lodash/last";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { articleMoreStoriesItemArticle$key } from "~/queries/__generated__/articleMoreStoriesItemArticle.graphql";

import {
  Section,
  StyledArticleMoreStoriesItem,
  StyledBaseImage,
  StyledEntityLink,
  Title,
} from "./styles";

export type Props = {
  className?: string;
  reference: articleMoreStoriesItemArticle$key;
};

export const ArticleMoreStoriesItem: FunctionComponent<Props> = ({ reference: reference_ }) => {
  const article = useFragment(
    graphql`
      fragment articleMoreStoriesItemArticle on Article {
        ...entityLink
        headline
        socialHeadline
        sections {
          value {
            name
            ...entityLink
          }
        }
        images {
          url
          size768x768: style(filter: { style: "768x768" }) {
            url
          }
        }
      }
    `,
    reference_,
  );

  const renderTitle = (article: Article) => (
    <Title reference={article}>
      {findFirstNonEmptyString(article.socialHeadline, article.headline)}
    </Title>
  );

  const renderImage = (article: Article) => {
    const mainImage = first(article.images);
    const source = mainImage?.size768x768?.url ?? mainImage?.url;
    return (
      source && (
        <StyledEntityLink reference={article}>
          <StyledBaseImage
            $aspectRatio="1"
            $objectFit="cover"
            alt={article.headline ?? undefined}
            src={source}
            disableLinkingData
          />
        </StyledEntityLink>
      )
    );
  };

  const renderSection = (article: Article) => {
    const sections = article.sections;
    const firstSection = first(sections);
    if (firstSection?.value) {
      const section = last(firstSection?.value);
      return section && <Section reference={section}>{section.name}</Section>;
    } else {
      return null;
    }
  };

  return (
    <StyledArticleMoreStoriesItem>
      {renderImage(article)}
      {renderSection(article)}
      {renderTitle(article)}
    </StyledArticleMoreStoriesItem>
  );
  type Article = Exclude<typeof article, undefined>;
};

ArticleMoreStoriesItem.displayName = "ArticleMoreStoriesItem";
